import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";

const PENDING_ACTIONS_KEY = "misty_driver_pending_actions";

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);

  // Load pending actions from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(PENDING_ACTIONS_KEY);
    if (stored) {
      try {
        const actions = JSON.parse(stored);
        setPendingActions(actions);
      } catch (error) {
        console.error("Failed to parse pending actions:", error);
        localStorage.removeItem(PENDING_ACTIONS_KEY);
      }
    }
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Connection restored");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error("Connection lost. Actions will be saved locally.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Save pending action
  const savePendingAction = useCallback(
    (action) => {
      const newAction = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action,
      };

      const updatedActions = [...pendingActions, newAction];
      setPendingActions(updatedActions);
      localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(updatedActions));

      toast("Action saved locally. Will retry when online.", {
        icon: "💾",
        duration: 3000,
      });
    },
    [pendingActions]
  );

  // Remove pending action
  // Functional update so several removals in one loop don't overwrite each other
  const removePendingAction = useCallback((actionId) => {
    setPendingActions((current) => {
      const updatedActions = current.filter((action) => action.id !== actionId);
      localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(updatedActions));
      return updatedActions;
    });
  }, []);

  // Clear all pending actions
  const clearPendingActions = useCallback(() => {
    setPendingActions([]);
    localStorage.removeItem(PENDING_ACTIONS_KEY);
  }, []);

  // Get pending actions count
  const pendingCount = pendingActions.length;

  return {
    isOnline,
    pendingActions,
    pendingCount,
    savePendingAction,
    removePendingAction,
    clearPendingActions,
  };
};
